import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { PageMeta } from "@/components/common/PageMeta";
import { ScrollSection } from "@/components/main-page/ScrollSection";
import { IRHero } from "@/components/ir/IRHero";
import { useCountUp } from "@/hooks/useCountUp";
// Turnstile disabled — rate limiting is sufficient for now. Re-enable if spam becomes an issue.
// import { TurnstileWidget } from "@/components/common/TurnstileWidget";

function MetricCell({ label, target, suffix }: { label: string; target: number; suffix: string }) {
  const { value, ref } = useCountUp(target);
  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className="p-5 bg-[var(--color-surface)]">
      <div className="text-xl md:text-2xl font-light tabular-nums font-[family-name:var(--font-mono)]">
        {value}{suffix}
      </div>
      <div className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] mt-1">
        {label}
      </div>
    </div>
  );
}

const LOCATIONS = [
  {
    region: "United Kingdom",
    status: "HQ",
    statusColor: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20",
    detail: "XENI live with beta clients. Robolab X launching with exclusive school partnership. Platform development and operations based in London.",
  },
  {
    region: "Australia & New Zealand",
    status: "Expanding",
    statusColor: "text-sky-500 bg-sky-500/10 border-sky-500/20",
    detail: "XENI deploying in April 2026. First international market.",
  },
  {
    region: "Singapore",
    status: "Expanding",
    statusColor: "text-sky-500 bg-sky-500/10 border-sky-500/20",
    detail: "XENI deploying in Southeast Asia. Second international market.",
  },
  {
    region: "United States",
    status: "Pipeline",
    statusColor: "text-amber-500 bg-amber-500/10 border-amber-500/20",
    detail: "Evaluating market entry. Largest global immigration and financial services market.",
  },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INPUT_BASE = "w-full px-3 py-2 border bg-transparent text-[var(--color-fg)] font-[family-name:var(--font-mono)] text-xs placeholder:text-[var(--color-fg-tertiary)] focus:outline-none transition-colors";
const INPUT_OK = `${INPUT_BASE} border-[var(--color-border)] focus:border-[var(--color-fg-secondary)]`;
const INPUT_ERR = `${INPUT_BASE} border-red-500/60 focus:border-red-500`;

type Errors = Partial<Record<"name" | "email", string>>;

function validate(form: { name: string; email: string }): Errors {
  const errors: Errors = {};
  if (!form.name.trim()) errors.name = "Name is required";
  if (!form.email.trim()) errors.email = "Email is required";
  else if (!EMAIL_RE.test(form.email)) errors.email = "Enter a valid email address";
  return errors;
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-[9px] text-red-400 mt-0.5 font-[family-name:var(--font-mono)]">{message}</p>;
}

function PitchDeckForm({ onBack }: { onBack?: () => void }) {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", message: "" });
  const [errors, setErrors] = useState<Errors>({});
  // const [turnstileToken, setTurnstileToken] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleSubmit = useCallback(async () => {
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          interest: "Pitch deck request",
          message: form.message || "I'd like to request access to the COSX pitch deck.",
          turnstileToken: "",
        }),
      });
      const data = await res.json() as { success?: boolean; error?: string };
      if (res.ok && data.success) {
        setSubmitted(true);
      } else {
        setSubmitError(data.error ?? "Something went wrong. Please try again.");
      }
    } catch {
      setSubmitError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [form]);

  if (submitted) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-6">
        <div className="text-lg mb-3 font-[family-name:var(--font-display)] font-light">
          Request sent
        </div>
        <p className="text-xs text-[var(--color-fg-secondary)] leading-relaxed max-w-[220px] mb-5">
          Thanks for your interest. We'll be in touch within one business day.
        </p>
        <button
          onClick={() => { setSubmitted(false); setForm({ name: "", email: "", organisation: "", message: "" }); onBack?.(); }}
          className="text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg)] transition-colors cursor-pointer"
        >
          ← Back to overview
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-[family-name:var(--font-mono)] font-medium">Request the pitch deck</h3>
        {onBack && (
          <button onClick={onBack} className="text-xs text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg)] transition-colors cursor-pointer">
            ← Back
          </button>
        )}
      </div>
      <div className="space-y-3 flex-1">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <input
              type="text" placeholder="Name *" value={form.name}
              onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); setErrors((e) => ({ ...e, name: undefined })); }}
              className={errors.name ? INPUT_ERR : INPUT_OK}
            />
            <FieldError message={errors.name} />
          </div>
          <div>
            <input
              type="email" placeholder="Email *" value={form.email}
              onChange={(e) => { setForm((f) => ({ ...f, email: e.target.value })); setErrors((e) => ({ ...e, email: undefined })); }}
              className={errors.email ? INPUT_ERR : INPUT_OK}
            />
            <FieldError message={errors.email} />
          </div>
        </div>
        <input
          type="text" placeholder="Fund / Organisation" value={form.organisation}
          onChange={(e) => setForm((f) => ({ ...f, organisation: e.target.value }))}
          className={INPUT_OK}
        />
        <textarea
          placeholder="Brief message (optional)" rows={2} value={form.message}
          onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
          className={`${INPUT_OK} resize-none`}
        />
        {/* <TurnstileWidget onToken={setTurnstileToken} /> */}
      </div>
      {submitError && (
        <p className="text-[10px] text-red-400 font-[family-name:var(--font-mono)] mb-2">{submitError}</p>
      )}
      <button
        onClick={handleSubmit}
        disabled={submitting}
        className="inline-flex items-center self-start px-5 py-2.5 rounded-full text-xs font-medium bg-[var(--color-fg)] text-[var(--color-bg)] hover:opacity-90 transition-opacity cursor-pointer mt-auto disabled:opacity-50"
      >
        {submitting ? "Sending..." : "Request access →"}
      </button>
    </div>
  );
}

export function IRLandingPage() {
  const [showDeckForm, setShowDeckForm] = useState(false);

  return (
    <>
      <PageMeta
        title="Investors | COSX"
        description="Learn about investing in COSX. AI agents for regulated industries. Request the pitch deck or access the investor portal."
        path="/ir"
      />

      <IRHero />

      {/* What we're building */}
      <ScrollSection id="opportunity" title="What we're building" compact>
        <div className="max-w-3xl space-y-5 mb-12">
          <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed">
            COSX builds and deploys AI agents for regulated industries. Immigration law, education, and financial services are our first three verticals. Each agent runs on a shared platform with built-in compliance, governance, and human oversight.
          </p>
          <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed">
            We don't just build the technology. We operate it. COSX agents run inside real businesses, processing real cases, with real compliance requirements. This gives us a feedback loop that pure software vendors don't have: every case improves the platform.
          </p>
          <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed">
            XENI, our immigration law agent, is live with beta clients in the UK and expanding to Australia. Robolab X is launching in education. Metaroom is in planning for investment management.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px border border-[var(--color-border)]">
          <MetricCell label="Live products" target={2} suffix="" />
          <MetricCell label="Countries" target={3} suffix="" />
          <MetricCell label="Beta clients" target={15} suffix="" />
          <MetricCell label="Pipeline cases" target={200} suffix="+" />
        </div>
      </ScrollSection>

      {/* Where we operate */}
      <ScrollSection id="presence" title="Where we operate" compact>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {LOCATIONS.map((loc) => (
            <div key={loc.region} className="border border-[var(--color-border)] p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium">{loc.region}</h3>
                <span className={`text-[9px] font-[family-name:var(--font-mono)] px-2 py-0.5 rounded border ${loc.statusColor}`}>
                  {loc.status}
                </span>
              </div>
              <p className="text-xs text-[var(--color-fg-secondary)] leading-relaxed">
                {loc.detail}
              </p>
            </div>
          ))}
        </div>
        <p className="text-sm text-[var(--color-fg-secondary)]">
          Team: Ben Zhang, Hugh Carnegie, George Yang, Cher Truman, and a 5-person engineering team.{" "}
          <Link to="/about#team" className="text-[var(--color-fg)] hover:underline">
            Meet the full team →
          </Link>
        </p>
      </ScrollSection>

      {/* Investor portal */}
      <ScrollSection id="portal" title="Investor portal" compact>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Request pitch deck — flip card */}
          <div className="h-[280px]" style={{ perspective: "1000px" }}>
            <div
              className="relative w-full h-full transition-transform duration-500"
              style={{
                transformStyle: "preserve-3d",
                transform: showDeckForm ? "rotateY(180deg)" : "rotateY(0)",
              }}
            >
              {/* Front — description */}
              <div
                className="absolute inset-0 border border-[var(--color-border)] p-6 flex flex-col"
                style={{ backfaceVisibility: "hidden" }}
              >
                <h3 className="text-sm font-[family-name:var(--font-mono)] font-medium mb-2">Request the pitch deck</h3>
                <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed mb-4">
                  Our deck covers the investment thesis, market opportunity, product roadmap, financials, and current round details.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {["Thesis", "Market", "Product", "Financials", "Team", "Round details"].map((tag) => (
                    <span key={tag} className="text-[10px] font-[family-name:var(--font-mono)] px-2 py-0.5 border border-[var(--color-border)] text-[var(--color-fg-tertiary)]">
                      {tag}
                    </span>
                  ))}
                </div>
                <button
                  onClick={() => setShowDeckForm(true)}
                  className="inline-flex items-center self-start px-5 py-2.5 rounded-full text-xs font-medium bg-[var(--color-fg)] text-[var(--color-bg)] hover:opacity-90 transition-opacity cursor-pointer mt-auto"
                >
                  Request access →
                </button>
              </div>
              {/* Back — form */}
              <div
                className="absolute inset-0 border border-[var(--color-border)] overflow-auto"
                style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
              >
                <PitchDeckForm onBack={() => setShowDeckForm(false)} />
              </div>
            </div>
          </div>

          {/* Dataroom access */}
          <div className="border border-[var(--color-border)] p-6 flex flex-col">
            <h3 className="text-sm font-[family-name:var(--font-mono)] font-medium mb-2">Investor dataroom</h3>
            <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed mb-4">
              Secure access to financial reports, legal documents, cap table, and quarterly investor updates.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {["Financials", "Legal docs", "Cap table", "Quarterly updates"].map((tag) => (
                <span key={tag} className="text-[10px] font-[family-name:var(--font-mono)] px-2 py-0.5 border border-[var(--color-border)] text-[var(--color-fg-tertiary)]">
                  {tag}
                </span>
              ))}
            </div>
            <button
              disabled
              className="inline-flex items-center self-start px-5 py-2.5 rounded-full text-xs font-medium border border-[var(--color-border)] text-[var(--color-fg-tertiary)] mt-auto cursor-not-allowed opacity-60"
            >
              Sign in to dataroom →
            </button>
          </div>
        </div>
      </ScrollSection>

      {/* Investor updates */}
      <ScrollSection id="updates" title="Investor updates" compact>
        <div className="border border-[var(--color-border)] overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="px-5 py-3 text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] font-medium">Date</th>
                <th className="px-5 py-3 text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] font-medium">Update</th>
                <th className="px-5 py-3 text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] font-medium text-right">Access</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-5 py-3 text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-secondary)]">Q1 2026</td>
                <td className="px-5 py-3 text-sm text-[var(--color-fg)]">Quarterly investor update</td>
                <td className="px-5 py-3 text-right">
                  <span className="text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] opacity-60 cursor-not-allowed">
                    Sign in to view →
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ScrollSection>

      {/* Contact */}
      <ScrollSection id="contact" title="Get in touch" subtitle="For investor enquiries, reach out directly." compact>
        <div className="flex flex-wrap gap-4">
          <a
            href="mailto:ir@cosx.co"
            className="inline-flex items-center px-6 py-3 rounded-full text-sm font-medium bg-[var(--color-fg)] text-[var(--color-bg)] hover:opacity-90 transition-opacity"
          >
            Email ir@cosx.co
          </a>
          <Link
            to="/platform"
            className="inline-flex items-center px-6 py-3 rounded-full text-sm font-medium border border-[var(--color-border)] text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] hover:border-[var(--color-fg-secondary)] transition-colors"
          >
            View the platform →
          </Link>
          <Link
            to="/products"
            className="inline-flex items-center px-6 py-3 rounded-full text-sm font-medium border border-[var(--color-border)] text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] hover:border-[var(--color-fg-secondary)] transition-colors"
          >
            See our products →
          </Link>
        </div>
      </ScrollSection>
    </>
  );
}
